// @flow

import {
  assoc,
  compose,
  curry,
  defaultTo,
  lensPath,
  lensProp,
  map,
  over,
  set,
  view,
} from "ramda";

import { type CurriedFn2 } from "@quantum/utils";

export type NotificationId = string;

export type Replacements = {
  [string]: string,
};

type NotificationAction<T> = {
  type: T,
  meta: NotificationId,
  payload?: Replacements,
};

export type OpenNotificationAction = NotificationAction<
  "quantum/ui@OPEN_NOTIFICATION"
>;
export type CloseNotificationAction = NotificationAction<
  "quantum/ui@CLOSE_NOTIFICATION"
>;

export type NotificationActions =
  | OpenNotificationAction
  | CloseNotificationAction;

export const openNotification: (
  NotificationId,
  ?Object
) => OpenNotificationAction = (notification, replacements) => ({
  type: "quantum/ui@OPEN_NOTIFICATION",
  meta: notification,
  payload: replacements || {},
});

export const closeNotification: NotificationId => CloseNotificationAction = notification => ({
  type: "quantum/ui@CLOSE_NOTIFICATION",
  meta: notification,
});

const notificationLens = lensProp("quantum/ui@notifications");
const notificationOpenLens = notification =>
  lensPath(["quantum/ui@notifications", notification, "open"]);
const notificationReplacementsLens = notification =>
  lensPath(["quantum/ui@notifications", notification, "replacements"]);

type State = {
  "quantum/ui@notifications": {
    [NotificationId]: {
      open: boolean,
      replacements: Replacements,
    },
  },
};

export const notificationReducer = (
  state: State,
  action: NotificationActions
): State => {
  switch (action.type) {
    case "quantum/ui@OPEN_NOTIFICATION": {
      const { meta: notification, payload: replacements } = action;
      return compose(
        set(notificationOpenLens(notification), true),
        set(notificationReplacementsLens(notification), replacements),
        over(
          notificationLens,
          compose(map(assoc("open", false)), defaultTo({}))
        )
      )(state);
    }

    case "quantum/ui@CLOSE_NOTIFICATION": {
      const { meta: notification } = action;
      return set(notificationOpenLens(notification), false, state);
    }

    default:
      return state;
  }
};

export const isNotificationOpen: CurriedFn2<
  NotificationId,
  State,
  boolean
> = curry((notification, state) =>
  view(notificationOpenLens(notification), state)
);

export const notificationReplacements: CurriedFn2<
  NotificationId,
  State,
  Replacements
> = curry(
  (notification, state) =>
    view(notificationReplacementsLens(notification), state) || {}
);
